import useAmplitude from '@eversports/amplitude-react/useAmplitude'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { BlockingError } from '../lib/custom-errors'
import getPhoenixUrlParams from '../lib/get-phoenix-url-params'

import {
  useUseCartCreationHandlerCreateCartFromEventBookableItemMutation,
  useUseCartCreationHandlerCreateCartFromPrivateSessionOfferMutation,
  useUseCartCreationHandlerCreateCartFromProductMutation,
  useUseCartCreationHandlerCreateCartFromVoucherMutation,
  useUseCartCreationHandlerFromCourtRentalMutation,
  useUseCartCreationHandlerTrackCheckoutStartMutation,
} from '../graphql'
import { getClientMetadata } from '../lib/get-client-metadata'
import sessionUtils from './session-utils'

interface Data {
  createdCartId?: string
}

const useCartCreationHandler = (): Data => {
  const [isCheckoutStartTracked, setIsCheckoutStartTracked] = useState(false)

  const location = useLocation()
  const clientMetadata = getClientMetadata()
  const { deviceId, sessionId } = useAmplitude()

  const {
    origin,
    bookableItemId,
    productId,
    venueId,
    start,
    end,
    amount,
    imageId,
    message,
    privateSessionOfferId,
    trainerId,
    courtRentalId,
  } = getPhoenixUrlParams(location.search)

  const [trackCheckoutStart] = useUseCartCreationHandlerTrackCheckoutStartMutation()

  const [createCartFromEventBookableItem, { data: eventBookableItemCart, error: eventBookableItemCartError }] =
    useUseCartCreationHandlerCreateCartFromEventBookableItemMutation({
      variables: { bookableItemId: bookableItemId as string, origin, clientMetadata },
    })

  const [createCartFromPrivateSessionOffer, { data: privateSessionOfferCart, error: privateSessionOfferCartError }] =
    useUseCartCreationHandlerCreateCartFromPrivateSessionOfferMutation({
      variables: {
        privateSessionOfferId: privateSessionOfferId as string,
        start: start as string,
        origin,
        trainerId,
        clientMetadata,
      },
    })

  const [createCartFromCourtRental, { data: courtRentalCart, error: courtRentalCartError }] =
    useUseCartCreationHandlerFromCourtRentalMutation({
      variables: {
        courtRentalId: courtRentalId as string,
        start: start as string,
        end: end as string,
        origin,
        clientMetadata,
      },
    })

  const [createCartFromProduct, { data: productCart, error: productCartError }] =
    useUseCartCreationHandlerCreateCartFromProductMutation({
      variables: { productId: productId as string, venueId: venueId as string, origin, clientMetadata },
    })

  const [createCartFromVoucher, { data: voucherCart, error: voucherCartError }] =
    useUseCartCreationHandlerCreateCartFromVoucherMutation({
      variables: {
        amount: amount as number,
        imageId: imageId as string,
        message: message as string,
        venueId: venueId as string,
        origin,
        clientMetadata,
      },
    })

  useEffect(() => {
    sessionUtils.removeStepper()
    sessionUtils.removeSetupIntentClientSecret()
    sessionUtils.removeConfirmationWasSuccessful()
    sessionUtils.setFamilyAccountReturnUrl(
      `/auth/family-account-login?redirectPath=${encodeURIComponent(
        window.location.pathname + window.location.search,
      )}`,
    )

    if (bookableItemId) {
      // eslint-disable-next-line  @typescript-eslint/no-floating-promises
      createCartFromEventBookableItem()
      return
    }

    if (privateSessionOfferId && start) {
      // eslint-disable-next-line  @typescript-eslint/no-floating-promises
      createCartFromPrivateSessionOffer()
      return
    }

    if (courtRentalId && start && end) {
      createCartFromCourtRental()
      return
    }

    if (productId && venueId) {
      // eslint-disable-next-line  @typescript-eslint/no-floating-promises
      createCartFromProduct()
      return
    }

    if (amount && venueId && imageId) {
      // eslint-disable-next-line  @typescript-eslint/no-floating-promises
      createCartFromVoucher()
      return
    }

    // No valid url params combination means a cart can't be created
    throw new Error(`Invalid url: ${location.pathname}${location.search}`)
  }, [])

  if (
    eventBookableItemCartError ||
    privateSessionOfferCartError ||
    productCartError ||
    voucherCartError ||
    courtRentalCartError
  ) {
    const error =
      eventBookableItemCartError ||
      privateSessionOfferCartError ||
      productCartError ||
      voucherCartError ||
      courtRentalCartError
    throw new Error(`Error creating Cart: ${error!.message}`)
  }

  if (eventBookableItemCart || privateSessionOfferCart || productCart || voucherCart || courtRentalCart) {
    const eventBookableItemCartExpectedError =
      eventBookableItemCart &&
      eventBookableItemCart.createCartFromEventBookableItem.__typename === 'ExpectedErrors' &&
      eventBookableItemCart.createCartFromEventBookableItem.errors[0]

    const privateSessionOfferCartExpectedError =
      privateSessionOfferCart &&
      privateSessionOfferCart.createCartFromPrivateSessionOffer.__typename === 'ExpectedErrors' &&
      privateSessionOfferCart.createCartFromPrivateSessionOffer.errors[0]

    const productCartExpectedError =
      productCart &&
      productCart.createCartFromProduct.__typename === 'ExpectedErrors' &&
      productCart.createCartFromProduct.errors[0]

    const voucherCartExpectedError =
      voucherCart &&
      voucherCart.createCartFromVoucher.__typename === 'ExpectedErrors' &&
      voucherCart.createCartFromVoucher.errors[0]

    const courtRentalCartExpectedError =
      courtRentalCart &&
      courtRentalCart.createCartFromCourtRental.__typename === 'ExpectedErrors' &&
      courtRentalCart.createCartFromCourtRental.errors[0]

    const expectedError =
      eventBookableItemCartExpectedError ||
      privateSessionOfferCartExpectedError ||
      productCartExpectedError ||
      voucherCartExpectedError ||
      courtRentalCartExpectedError

    // In this case we just want to treat the expected error the same as a normal one
    // Due to always being a breaking error for the cart creation
    if (expectedError) {
      throw new BlockingError('There was a problem creating the cart', expectedError.message)
    }

    const eventBookableItemCartId =
      eventBookableItemCart &&
      eventBookableItemCart.createCartFromEventBookableItem.__typename === 'Cart' &&
      eventBookableItemCart.createCartFromEventBookableItem.id

    const privateSessionOfferCartId =
      privateSessionOfferCart &&
      privateSessionOfferCart.createCartFromPrivateSessionOffer.__typename === 'Cart' &&
      privateSessionOfferCart.createCartFromPrivateSessionOffer.id

    const productCartId =
      productCart && productCart.createCartFromProduct.__typename === 'Cart' && productCart.createCartFromProduct.id

    const voucherCartId =
      voucherCart && voucherCart.createCartFromVoucher.__typename === 'Cart' && voucherCart.createCartFromVoucher.id

    const courtRentalCartId =
      courtRentalCart &&
      courtRentalCart.createCartFromCourtRental.__typename === 'Cart' &&
      courtRentalCart.createCartFromCourtRental.id

    const createdCartId = (eventBookableItemCartId ||
      privateSessionOfferCartId ||
      productCartId ||
      voucherCartId ||
      courtRentalCartId) as string

    if (deviceId && sessionId && !isCheckoutStartTracked) {
      trackCheckoutStart({
        variables: { cartId: createdCartId, deviceId, sessionId: Number(sessionId) },
      })
      setIsCheckoutStartTracked(true)
    }

    return { createdCartId }
  }

  return { createdCartId: undefined }
}

export default useCartCreationHandler
